import { useCallback, useState } from 'react';

import type { ICPQCart, ICPQItem } from 'api/cart/types';
import { useAdditionalDataStorage } from 'containers/AdditionalDataStorage/context';
import { useKitWorkspace } from 'containers/KitWorkspaceStorage/context';
import type { IAssembly, IKit, IKitAccessory } from 'containers/KitWorkspaceStorage/types';
import { getCommitAction } from 'common/constants';
import { CommitAction } from 'utils/commit-action';
import { sendDataToParentIframe } from 'utils/send-data-to-parent-iframe';
import { readBool } from 'utils/readBool';
import { showToast } from 'utils/toasts';
import { ToastTypes } from 'common/types/toasts';
import { assemblyQty } from './assemblySummary';
import { useKitTranslate } from './useKitTranslate';

const DEFAULT_CART_GROUP = '@cpq';

// ARG runs the "layout2" last-page variant (set per rule set via Content -> i18n)
// where the cart dispatch is surfaced as a "Complete My Assembly" button instead
// of "Add to Cart". The legacy group flow gated that button on this same layout
// flag (a per-account rule_set signal), NOT on the build-time commit action, so
// mirror it in the kit flow: show the button for layout2 regardless of the commit
// action, and reuse ARG's existing label override.
const COMPLETE_ASSEMBLY_LAYOUT = 'layout2';
const COMPLETE_ASSEMBLY_LABEL_KEY = 'texts.assemblyGroup.addToCartBtn';
const CART_LABEL_KEY = 'texts.kits.final.addToCart';
// One toast, however often the button is pressed outside a host page.
const NOT_EMBEDDED_TOAST_ID = 'kit-cart-not-embedded';

// Loose "Add parts" companions become plain accessory line items on the cart.
const accessoriesToItems = (accessories: IKitAccessory[]): ICPQItem[] => accessories.map((acc): ICPQItem => ({
  type: 'main',
  sku: acc.partNumber,
  quantity: acc.qty,
  title: acc.partNumber,
  description: acc.description,
  image: acc.imageUrl,
}));

// Turn one assembly into its cart event: strip the FE-added sessionId (the parent
// iframe contract carries no session ids), then enrich it with data that lives on
// the workspace, not in the persisted cartData — the current assembly qty, the
// assembly notes, and any loose accessories (its own + kit-level ones passed in).
const toCartEvent = (assembly: IAssembly, loose: IKitAccessory[]): ICPQCart => {
  const cart = { ...assembly.cartData };
  delete (cart as { sessionId?: unknown }).sessionId;

  cart.quantity = assemblyQty(assembly);
  if (assembly.notes) {
    cart.notes = assembly.notes;
  }
  const looseItems = accessoriesToItems([...(assembly.accessories ?? []), ...loose]);
  if (looseItems.length > 0) {
    cart.accessories = [...(cart.accessories ?? []), ...looseItems];
  }

  return cart;
};

// Flatten every configured assembly in the workspace (loose standalone + every
// kit line) into the cart payload list. Kit-level loose accessories ride with the
// kit's first line so they are sent once.
const collectCarts = (standalone: IAssembly[], kits: IKit[]): ICPQCart[] => {
  const carts = standalone.map((assembly) => toCartEvent(assembly, []));
  kits.forEach((kit) => kit.lines.forEach((line, index) => {
    carts.push(toCartEvent(line.assembly, index === 0 ? (kit.accessories ?? []) : []));
  }));

  return carts;
};

/**
 * Shared "Add to Cart" dispatch for the kit final screen. Reuses the exact same
 * parent-iframe contract as the legacy/group flow: it posts the workspace's
 * assemblies through `sendDataToParentIframe` (one-by-one when
 * `summaryICPQCartEventSeparated`, otherwise as a single ICPQCart[]), under the
 * `summaryICPQCartGroup` message type. `cartEnabled` follows the tenant commit
 * action so the button only appears when the parent expects the cart dispatch.
 */
export const useKitCartCommit = () => {
  const t = useKitTranslate();
  const { kits, standalone } = useKitWorkspace();
  const { data: additionalData } = useAdditionalDataStorage();

  const [isSendingCart, setIsSendingCart] = useState(false);

  // ARG (layout2) surfaces the cart dispatch as "Complete My Assembly" and shows
  // it independent of the build-time commit action, same as the legacy flow.
  const isCompleteAssemblyLayout = t('variables.summaryButtonsLayout') === COMPLETE_ASSEMBLY_LAYOUT;
  const cartEnabled = getCommitAction() === CommitAction.SEND_TO_PARENT_IFRAME
    || isCompleteAssemblyLayout;
  const cartLabel = isCompleteAssemblyLayout
    ? t(COMPLETE_ASSEMBLY_LABEL_KEY)
    : t(CART_LABEL_KEY);

  const onAddToCart = useCallback(async () => {
    if (window.self === window.parent) {
      showToast(ToastTypes.error, t('texts.kits.toast.cartOnlyWhenEmbedded'), NOT_EMBEDDED_TOAST_ID);
      return;
    }
    setIsSendingCart(true);
    try {
      const summaryEventSeparated = readBool('variables.summaryICPQCartEventSeparated');
      const summaryMsgType = t('variables.summaryICPQCartGroup') || DEFAULT_CART_GROUP;
      const carts = collectCarts(standalone, kits);
      if (carts.length === 0) return;

      if (summaryEventSeparated) {
        for (let i = 0; i < carts.length; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          const { message, redirectUrl } = await sendDataToParentIframe<ICPQCart>(
            carts[i],
            additionalData,
            (cart) => cart,
            summaryMsgType,
          );
          if (message) {
            // eslint-disable-next-line no-alert
            alert(message);
          }
          if (redirectUrl) {
            window.location.href = redirectUrl;
            return;
          }
        }
      } else {
        const { message, redirectUrl } = await sendDataToParentIframe<ICPQCart[]>(
          carts,
          additionalData,
          (list) => list,
          summaryMsgType,
        );
        if (message) {
          // eslint-disable-next-line no-alert
          alert(message);
        }
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
      }
    } catch (err: any) {
      showToast(ToastTypes.error, String(err?.message || err));
    } finally {
      setIsSendingCart(false);
    }
  }, [standalone, kits, additionalData, t]);

  return {
    cartEnabled, cartLabel, onAddToCart, isSendingCart,
  };
};

export default useKitCartCommit;
